/**
 * Help Center content — Part 2: Checklists, Drills, Defects.
 * Imported by helpContent/index.js; do not import directly.
 */

// ─── 3. CHECKLISTS ───────────────────────────────────────────────────────────

export const checklists = {
  id: 'checklists',
  title: '3. Checklists',
  summary: 'Run and record routine and emergency checklists, keeping a permanent locked audit record.',
  keywords: ['checklist', 'daily checks', 'emergency', 'navigation', 'operational', 'audit', 'lock', 'history', 'voyage', 'fire', 'man overboard', 'abandon ship'],
  steps: [
    {
      text: 'Checklists let the crew run and record the vessel\'s routine and emergency procedures — daily engine/safety checks, navigation procedures, and emergency response — and keep a permanent, timestamped record of each completed one. Completed checklists are locked for audit compliance and carry regulatory references (WBC3, COLREGs, SOLAS, MARPOL, STCW, ISM). They live under left menu → Operations → Checklists tab.',
      image: 'checklist-02-operations-landing.png',
    },
    {
      text: 'Open Operations, click the Checklists tab. It has two sub-tabs — Categories and History — plus a "Search checklists..." box.',
      image: 'checklist-03-checklists-tab.png',
    },
    {
      text: 'The Categories view shows four cards: Emergency (11 types) · Navigation (5 types) · Operational · Daily Checks ("0/1 today").',
      image: 'checklist-03-categories.png',
    },
    {
      text: 'Click the Daily Checks card, then the Daily Checks — 14 checks card to open it. If a voyage is active, a blue "Linking to Active Voyage #…" banner with an Auto tag appears at the top.',
      image: 'checklist-04-daily-open.png',
    },
    {
      text: 'Tap each item to tick it (it turns green); the Progress counter and bar climb (0/14 → 14/14). Each item has a small note icon, and there\'s a General Notes (optional) box at the bottom.',
      image: 'checklist-05-daily-items.png',
    },
    {
      text: 'When all items are ticked, tap the green Complete Daily Checks button. Under it a warning reads "Record will be locked after submission for audit compliance."',
      image: 'checklist-07-all-ticked.png',
    },
    {
      text: 'You return to Categories — the Daily Checks tile now reads "1/1 today" and the record appears under a RECENT strip.',
      image: 'checklist-08-after-complete.png',
    },
    {
      text: 'Open the History sub-tab — a table of Date · Checklist · Type · Completed By · Status (your record shows "All passed").',
      image: 'checklist-09-history.png',
    },
    {
      text: 'Open the record — a read-only detail badged "Locked for Audit" with the linked voyage, Completion Status 14/14, your Notes, and an Audit Information block (Record ID, Created timestamp, Version 1, Status: Locked). Only a Close button — it cannot be edited.',
      image: 'checklist-10-record-detail.png',
    },
    {
      text: 'Emergency category (11 procedures): Fire Response, Man Overboard, Abandon Ship, etc. — each with a regulatory reference and the same tick-and-complete flow.',
      image: 'checklist-11-emergency.png',
    },
    {
      text: 'Navigation category (5 procedures): Watch Handover, Heavy Weather, Restricted Visibility, Confined Waters, Night Passage — each with a short "when to use" note.',
      image: 'checklist-12-navigation.png',
    },
  ],
};

// ─── 4. DRILLS ───────────────────────────────────────────────────────────────

export const drills = {
  id: 'drills',
  title: '4. Drills',
  summary: 'Record safety drills and onboard training, track due dates, and keep a signed audit history.',
  keywords: ['drill', 'fire', 'man overboard', 'abandon ship', 'training', 'sign off', 'record', 'history', 'participant', 'frequency', 'schedule'],
  steps: [
    {
      text: 'The Drills module (page title "Drills & Training") is where you record that safety drills and onboard training were carried out, track when each drill is next due, and keep a signed-off, audit-trailed history of every drill. Recording a drill automatically pushes its "next due" date out by its frequency. It has three tabs: Safety Drills, Onboard Training, Drill History. Reach it from left menu → Drills.',
      image: 'drill-01-landing.png',
    },
    {
      text: 'Click + Record Drill (Quick Actions) — or click a drill card (e.g. Fire) to open the form with that type pre-selected.',
      image: 'drill-02-record-form.png',
    },
    {
      text: 'Pick a Drill Type (★ = registered, * = required; e.g. Fire Drill, Abandon Ship Drill, Man Overboard Drill).',
      image: 'drill-02b-drilltype-dropdown.png',
    },
    {
      text: 'Confirm Date (defaults to today), and optionally set Time, Duration (minutes) (default 30) and Location. Under Participants, tap crew cards to select them (or Select All). Set Outcome (default "Successful") and add Notes.',
      image: 'drill-03-form-filled.png',
    },
    {
      text: 'Tick "Sign off this drill now" ("As Master/Officer, I confirm this drill was conducted satisfactorily"), then tap Save Drill Record. A "Drill Recorded" dialog confirms it — click OK.',
      image: 'drill-04-after-save.png',
    },
    {
      text: 'Back on Safety Drills, the drill you recorded now shows Last: = today\'s date, and its due badge has moved out by the frequency (e.g. Monthly → "Due in ~30 days"). Open Drill History — your drill is at the top with participant count, who/when, and Successful + Signed Off badges.',
      image: 'drill-06-history.png',
    },
    {
      text: 'Click a history row to open the read-only Drill Record — sign-off banner, Drill Information, Participants list, Notes, an Audit Trail and a Record ID in the footer (no Edit/Delete — it\'s a permanent record).',
      image: 'drill-08-history-row-click.png',
    },
    {
      text: 'The Onboard Training tab is a separate training-records area with a Training Summary and + Add Training / Export / History actions.',
      image: 'drill-10-onboard-training.png',
    },
  ],
};

// ─── 5. DEFECTS ──────────────────────────────────────────────────────────────

export const defects = {
  id: 'defects',
  title: '5. Defects',
  summary: 'Log equipment issues, track them through their lifecycle, and formally close them out.',
  keywords: ['defect', 'report', 'risk level', 'priority', 'corrective job', 'audit trail', 'close', 'open', 'in progress', 'resolved', 'edit', 'evidence', 'photo'],
  steps: [
    {
      text: 'The Defects module logs equipment issues and repairs: report a defect, set its risk/priority, link it to an Equipment Register item, attach evidence photos, track it through its lifecycle (Open → In Progress → Resolved → Closed), and optionally raise a one-time Corrective Job to fix it. Every change is recorded in a Status History and Audit Trail, and once a defect is Closed it cannot be modified (audit integrity). Reach it from left menu → Defects.',
      image: 'defect-01-landing.png',
    },
    {
      text: 'Click Report Defect. The two required fields are Date Reported and Reported By (crew dropdown).',
      image: 'defect-02-report-form.png',
    },
    {
      text: 'Write a clear Description and a Location, choose a Risk Level (Critical / High / Medium / Low), and — optionally — pick an item under Equipment Linked to tie the defect to a piece of kit.',
      image: 'defect-02c-form-filled.png',
    },
    {
      text: 'Under Evidence Photos, use Take photo or Choose from gallery to attach an image. Optionally set Immediate Action Taken / Fixed Immediately / Assigned To / Status, then click Report Defect. The header shows "Syncing → Synced" and the defect appears in the Active list.',
      image: 'defect-03-after-submit.png',
    },
    {
      text: 'Click the defect card to open its detail (Defect #DEF-…). You see Basic Information, a Status & Priority panel, Status History and Audit Trail, plus action buttons Generate Corrective Job · Edit · Delete.',
      image: 'defect-04-detail.png',
    },
    {
      text: 'Use the status buttons: Mark In Progress, then Mark as Resolved (which shows "Resolved · AWAITING CLOSURE" and a Reopen Defect button).',
      image: 'defect-09-resolve-prompt.png',
    },
    {
      text: 'Generate a corrective job (optional): From the detail, click Generate Corrective Job to open a One-Time Corrective job form (Item Name, Category, Assigned to, Requirement/Standard, Purpose, Scope of Work, Notes). It "will not recur after completion" and links back to the defect. Tap Add Job.',
      image: 'defect-05-corrective-job.png',
    },
    {
      text: 'Edit gate: If you need to change an open defect, Edit first asks for a Reason for Edit (minimum 5 characters) — "All changes to safety-critical records must be documented… for MCA inspections." Enter a reason, then Continue to Edit, change fields, and Update Defect.',
      image: 'defect-07-edit-warning.png',
    },
    {
      text: 'Close the defect (formal closeout): Click Close Defect to open the WBC3 closeout form: Closed By, Root Cause (dropdown), Corrective Action Taken, Verification/Testing Method. The Close Defect button stays disabled until all required fields are filled.',
      image: 'defect-11-close-prompt.png',
    },
    {
      text: 'Once closed, the defect leaves the Active list and appears in Defect History. The closed detail is a read-only report (Defect Details, Evidence Photos, Resolution, Closure Details) with no Edit/Delete/Reopen — it\'s locked.',
      image: 'defect-16-closed-detail.png',
    },
  ],
};
